import api from './api';
import type { Channel } from './releases';

// W6 corte 18.A (2026-05-11) — service do painel pra CRUD de client_builds.
//
// Endpoints admin (backend src/controllers/admin/ClientBuildsController.h):
//   POST   /admin/client-builds          → Create
//   GET    /admin/client-builds          → List (cursor pagination)
//   GET    /admin/client-builds/{uuid}   → GetByUuid (Sprint 18.G)
//   PATCH  /admin/client-builds/{uuid}   → Update parcial
//   DELETE /admin/client-builds/{uuid}   → Soft delete

export interface ClientBuild {
  id: string;
  name: string;
  version: string;
  channel: Channel;
  is_active: boolean;
  text_hash_sha256: string;
  exe_path_hint: string;
  notes: string;
  min_required_version: string;
  activated_at: string;
  created_at: string;
}

export interface ListClientBuildsParams {
  cursor?: number;
  limit?: number;
  name?: string;
}

export interface ListClientBuildsResponse {
  data: ClientBuild[];
  next_cursor?: number;
}

export interface CreateClientBuildInput {
  name: string;                  // ex: "AIKA"
  version: string;               // semver "2.0.0"
  channel?: Channel;             // default "stable"
  text_hash_sha256: string;      // hex 64 chars
  exe_path_hint?: string;
  notes?: string;
  is_active?: boolean;           // default false (operador ativa explícito)
  min_required_version?: string; // semver — força update se SDK reporta < min
  // Sprint Auto-update F1 — preenchido após upload R2 (F6)
  r2_object_key?: string;
  antihack_min_version?: string;
}

// Sprint Auto-update F6 — presigned PUT URL R2 pra upload binary direto.
export interface UploadUrlRequest {
  name: string;
  version: string;
  channel?: Channel;
  filename?: string;       // default "<name>.exe"
  ttl_seconds?: number;    // default 900, clamp [60, 86400]
}

export interface UploadUrlResponse {
  presigned_put_url: string;
  r2_object_key: string;
  expires_at: string;      // ISO 8601 UTC
}

export async function generateUploadUrl(
  input: UploadUrlRequest,
): Promise<UploadUrlResponse> {
  const resp = await api.post<UploadUrlResponse>(
    '/admin/client-builds/upload-url', input);
  return resp.data;
}

export interface UpdateClientBuildInput {
  channel?: Channel;
  text_hash_sha256?: string;
  exe_path_hint?: string;
  notes?: string;
  is_active?: boolean;
  min_required_version?: string;
  r2_object_key?: string;
  antihack_min_version?: string;
}

export async function listClientBuilds(
  params: ListClientBuildsParams = {},
): Promise<ListClientBuildsResponse> {
  const resp = await api.get<ListClientBuildsResponse>('/admin/client-builds', {
    params: {
      cursor: params.cursor || 0,
      limit: params.limit || 50,
      name: params.name || undefined,
    },
  });
  return resp.data;
}

export async function getClientBuild(uuid: string): Promise<ClientBuild> {
  const resp = await api.get<ClientBuild>(`/admin/client-builds/${uuid}`);
  return resp.data;
}

export async function createClientBuild(input: CreateClientBuildInput): Promise<ClientBuild> {
  const resp = await api.post<ClientBuild>('/admin/client-builds', input);
  return resp.data;
}

export async function updateClientBuild(
  uuid: string,
  input: UpdateClientBuildInput,
): Promise<ClientBuild> {
  const resp = await api.patch<ClientBuild>(`/admin/client-builds/${uuid}`, input);
  return resp.data;
}

export async function deleteClientBuild(uuid: string): Promise<void> {
  await api.delete(`/admin/client-builds/${uuid}`);
}
